// import dateFnsFormat from 'date-fns/format'
import { formatInTimeZone } from 'date-fns-tz'
import get from 'lodash/get'
import {
  getDateTickPixelInterval,
  getDateMinRange,
  getDateLabelFormat,
  formatCurrency,
  roundDate,
  getDateTooltipFormat,
  formatTooltipCurrency,
} from './formatHelpers'
import { seriesConfig, yAxisConfig } from './config'

export const getXAxisConfig = (
  config: any,
  choosenFrame: string,
  axisNum: number
) => {
  return {
    ...config.xAxis[axisNum],
    tickPixelInterval: getDateTickPixelInterval(choosenFrame),
    minRange: getDateMinRange(choosenFrame),
    labels: {
      ...config.xAxis[axisNum].labels,
      formatter: function () {
        return `<span class="Chart-label-x">${formatInTimeZone(
          new Date(this.value),
          'Europe/Paris',
          getDateLabelFormat(choosenFrame)
        )}</span>`
      },
    },
  }
}

export const getTooltipConfig = (
  config: any,
  isMobile: boolean,
  choosenFrame: string,
  width: number
) => {
  const seriesFormatter = {
    Price: {
      name: (name: string) => `${name}`,
      value: (v: number) => `${formatTooltipCurrency(v)} USD`,
    },
    'Market Cap': {
      name: (name: string) => `${name}`,
      value: (v: number) => `${formatTooltipCurrency(v)} USD`,
    },
    Volume: {
      name: (name: string) => `${name} 24h`,
      value: (v: number) => `${formatTooltipCurrency(v)} USD`,
    },
  }

  return {
    ...config.tooltip,
    backgroundColor: 'rgba(255, 255, 255, 0)',
    positioner: (labelWidth: number, labelHeight: number, point: any) => {
      let x
      if (isMobile) {
        if (point.plotX < width * 0.35) {
          x = point.plotX
        } else if (point.plotX < width * 0.75) {
          x = point.plotX - labelWidth / 2
        } else {
          x = point.plotX - labelWidth
        }
      } else {
        if (point.plotX < width * 0.5) {
          x = point.plotX + labelWidth / 4 + 10
        } else {
          x = point.plotX - labelWidth / 1.25
        }
      }

      return {
        x,
        y: point.plotY + labelHeight / 2,
      }
    },
    formatter: function () {
      const datetime = roundDate(choosenFrame, new Date(this.x))
      return `<div class="Chart-tooltip-y">
              <div class="Chart-tooltip-y-header">
                  ${formatInTimeZone(
                    datetime,
                    'Europe/Paris',
                    getDateTooltipFormat()
                  )}
              </div>
              <div>
                ${this.points
                  .map((point: any) => {
                    const nameFormatter = get(
                      seriesFormatter,
                      [point.series.name, 'name'],
                      (v: any) => v
                    )
                    const valueFormatter = get(
                      seriesFormatter,
                      [point.series.name, 'value'],
                      (v: any) => v
                    )
                    return `
                  <div class="Chart-tooltip-y-text">
                    <div class="Chart-tooltip-y-series">
                        <div class="Chart-tooltip-y-series-dot" style="background-color: ${
                          point.series.color
                        }"></div> ${nameFormatter(point.series.name)}:
                    </div>
                    <div class="Chart-tooltip-y-value">
                        ${valueFormatter(point.y)}
                    </div>
                  </div>`
                  })
                  .join('')}
              </div>
            </div>`
    },
  }
}

export const getPlotOptionsConfig = (
  config: any,
  isMobile: boolean,
  isSafari: boolean
) => {
  return {
    ...config.plotOptions,
    area: {
      ...config.plotOptions.area,
      fillColor:
        isMobile || isSafari
          ? 'rgba(255, 255, 255, 0.25)'
          : {
              linearGradient: { x1: 0, x2: 0, y1: 0, y2: 1 },
              stops: [
                [0, 'rgba(255, 255, 255, 0.25)'],
                [1, 'rgb(255, 255, 255, 0.05)'],
              ],
            },
    },
  }
}

export const getExportingConfig = (
  config: any,
  coin: any,
  symbol: string,
  price: number,
  choosenFrame: string,
  fullScreen: boolean,
  isMobile: boolean
) => {
  let contextButtonX = -5
  if (isMobile) {
    contextButtonX = fullScreen ? -15 : 0
  }

  return {
    ...config.exporting,
    fallbackToExportServer: false,
    filename: `${coin}-${choosenFrame}`,
    buttons: {
      ...config.exporting.buttons,
      contextButton: {
        ...config.exporting.buttons.contextButton,
        x: contextButtonX,
      },
    },
    chartOptions: {
      ...config.exporting.chartOptions,
      chart: {
        ...config.chart,
        spacing: [0, 15, 0, 15],
      },
      title: {
        text: `<div style="display: flex;"><img src="/icons/slug-coins/32x32/bitcoin-btc.png" />
          <div style="margin-left: 10px;">
            <span style="font-size:12px;line-height:30px;font-family: "Roboto", sans-serif;">${coin}</span>
            <span style="font-size:10px;color:rgba(255, 255, 255, 0.5); font-family: "Roboto", sans-serif;">(${
              symbol ? symbol.toUpperCase() : ''
            })</span>
          </div></div>`,
        style: {
          color: '#fff',
          fontSize: '16px',
          textTransform: 'capitalize',
        },
        align: 'left',
        floating: false,
        margin: -30,
        y: 30,
        useHTML: true,
      },
      subtitle: {
        text: `<span style="line-height:30px; font-size: 14px;font-family: "Roboto", sans-serif;">${formatCurrency(
          price
        )} USD</span>`,
        style: { color: '#fff', fontSize: '16px ' },
        align: 'right',
        floating: false,
        margin: -30,
        y: 30,
        useHTML: true,
      },
    },
  }
}

export const getChartConfig = (
  config: any,
  fullScreen: boolean,
  isMobile: boolean,
  height: number
) => {
  return {
    ...config.chart,
    spacing: fullScreen
      ? [10, 0, 0, 0]
      : isMobile
      ? [0, 22, 0, 22]
      : config.chart.spacing,
    height: height,
  }
}

export const getYAxisConfig = (
  dataIndex: number,
  isMobile: boolean,
  DataProviders: any
) => {
  const emptyDataProvider: any = []
  const data = get(DataProviders, dataIndex, emptyDataProvider)
  const minValue = Math.min.apply(
    null,
    data.map(i => i.y)
  )
  const maxValue = Math.max.apply(
    null,
    data.map(i => i.y)
  )

  const diff = maxValue - minValue

  const minY = minValue - 0.1 * diff
  const maxY = maxValue + 0.1 * diff

  const marketCapAxis = dataIndex === 0

  let align = undefined
  let x = undefined
  if (isMobile) {
    align = 'left'
    x = 0
  } else {
    align = 'right'
    x = marketCapAxis ? -10 : 65
  }

  const cfg = yAxisConfig()[dataIndex]

  return {
    ...cfg,
    tickAmount: marketCapAxis ? 5 : 3,
    type: 'linear',
    title: {
      ...cfg.title,
      text: isMobile ? '' : cfg.title.text,
    },
    labels: {
      ...cfg.labels,
      align,
      x,
      reserveSpace: !isMobile,
      formatter: function () {
        const color = marketCapAxis ? '#84c47c' : '#777777'
        const value = formatCurrency(this.value)
        const text = `$${value === '—' ? 0 : value}`
        return `<span class="Chart-label-y" style="color: ${color};">${text}</span>`
      },
    },
    floor: minY,
    max: maxY,
    min: 0,
  }
}

export const getSeriesConfig = () => {
  return seriesConfig()
}
